/**
 * DatePicker — 캘린더 날짜 선택기
 *
 * 커스텀 캘린더 팝오버로 날짜를 선택한다.
 * 외부 라이브러리 없이 순수 구현.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import styles from './DatePicker.module.css'

export interface DatePickerProps {
  /** 선택된 날짜 (YYYY-MM-DD 문자열 또는 Date) */
  value?: string | Date | null
  /** 날짜 변경 콜백 */
  onChange?: (date: Date | null, formatted: string) => void
  /** 필드 레이블 */
  label?: string
  /** 에러 메시지 */
  error?: string
  /** 보조 안내 텍스트 */
  helperText?: string
  /** 플레이스홀더 */
  placeholder?: string
  /** 날짜 포맷 (표시용). 기본 'yyyy년 M월 d일' */
  displayFormat?: (date: Date) => string
  /** 최소 선택 가능 날짜 */
  minDate?: Date
  /** 최대 선택 가능 날짜 */
  maxDate?: Date
  /** 입력 크기 */
  size?: 'sm' | 'md'
  /** 비활성 상태 */
  disabled?: boolean
  /** 추가 클래스명 */
  className?: string
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

export function toDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null
  if (v instanceof Date) return v
  const d = new Date(v + 'T00:00:00')
  return isNaN(d.getTime()) ? null : d
}

/** datetime 문자열/Date를 파싱 (시간 포함) */
export function toDateTime(v: string | Date | null | undefined): Date | null {
  if (!v) return null
  if (v instanceof Date) return v
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}

export function formatISO(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultFormat(d: Date): string {
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

export function getCalendarDays(year: number, month: number): { date: Date; currentMonth: boolean }[] {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = getDaysInMonth(year, month)
  const daysInPrevMonth = month === 0 ? getDaysInMonth(year - 1, 11) : getDaysInMonth(year, month - 1)

  const days: { date: Date; currentMonth: boolean }[] = []

  // 이전 달 날짜
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrevMonth - i
    const prevMonth = month === 0 ? 11 : month - 1
    const prevYear = month === 0 ? year - 1 : year
    days.push({ date: new Date(prevYear, prevMonth, d), currentMonth: false })
  }

  // 현재 달 날짜
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: new Date(year, month, d), currentMonth: true })
  }

  // 다음 달 날짜 (6주 = 42일 채우기)
  const remaining = 42 - days.length
  for (let d = 1; d <= remaining; d++) {
    const nextMonth = month === 11 ? 0 : month + 1
    const nextYear = month === 11 ? year + 1 : year
    days.push({ date: new Date(nextYear, nextMonth, d), currentMonth: false })
  }

  return days
}

export function DatePicker({
  value,
  onChange,
  label,
  error,
  helperText,
  placeholder = '날짜를 선택하세요',
  displayFormat = defaultFormat,
  minDate,
  maxDate,
  size = 'md',
  disabled = false,
  className,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const selected = toDate(value)
  const today = useMemo(() => new Date(), [])

  // 캘린더 표시 월 (선택된 날짜 또는 오늘 기준)
  const [viewYear, setViewYear] = useState(() => (selected ?? today).getFullYear())
  const [viewMonth, setViewMonth] = useState(() => (selected ?? today).getMonth())

  const wrapperRef = useRef<HTMLDivElement>(null)

  // 선택 값이 바뀌면 뷰를 추적
  useEffect(() => {
    if (selected) {
      setViewYear(selected.getFullYear())
      setViewMonth(selected.getMonth())
    }
  }, [selected])

  const calendarDays = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth])

  const isDisabledDate = useCallback((d: Date) => {
    if (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) return true
    if (maxDate && d > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())) return true
    return false
  }, [minDate, maxDate])

  const handlePrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) { setViewYear((y) => y - 1); return 11 }
      return m - 1
    })
  }, [])

  const handleNextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) { setViewYear((y) => y + 1); return 0 }
      return m + 1
    })
  }, [])

  const handleSelectDay = useCallback((d: Date) => {
    if (isDisabledDate(d)) return
    onChange?.(d, formatISO(d))
    setOpen(false)
  }, [isDisabledDate, onChange])

  const handleToggle = useCallback(() => {
    if (!disabled) setOpen((v) => !v)
  }, [disabled])

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // ESC 키로 닫기
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper, className)}>
      {label && <label className={styles.label}>{label}</label>}

      <button
        type="button"
        className={cn(
          styles.trigger,
          styles[size],
          disabled && styles.disabled,
          error && styles.triggerError,
        )}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={cn(styles.triggerText, !selected && styles.placeholder)}>
          {selected ? displayFormat(selected) : placeholder}
        </span>
        <svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={styles.popover}>
          {/* 헤더: 월 이동 */}
          <div className={styles.calendarHeader}>
            <button type="button" className={styles.navBtn} onClick={handlePrevMonth}>
              <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                <path d="M10.354 3.646a.5.5 0 010 .708L6.707 8l3.647 3.646a.5.5 0 01-.708.708l-4-4a.5.5 0 010-.708l4-4a.5.5 0 01.708 0z" />
              </svg>
            </button>
            <span className={styles.monthTitle}>
              {viewYear}년 {viewMonth + 1}월
            </span>
            <button type="button" className={styles.navBtn} onClick={handleNextMonth}>
              <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                <path d="M5.646 3.646a.5.5 0 01.708 0l4 4a.5.5 0 010 .708l-4 4a.5.5 0 01-.708-.708L9.293 8 5.646 4.354a.5.5 0 010-.708z" />
              </svg>
            </button>
          </div>

          {/* 요일 헤더 */}
          <div className={styles.weekdays}>
            {WEEKDAYS.map((w) => (
              <span key={w} className={styles.weekday}>{w}</span>
            ))}
          </div>

          {/* 날짜 그리드 */}
          <div className={styles.daysGrid}>
            {calendarDays.map(({ date, currentMonth }, i) => {
              const isSelected = selected ? isSameDay(date, selected) : false
              const isToday = isSameDay(date, today)
              const isDayDisabled = isDisabledDate(date)

              return (
                <button
                  key={i}
                  type="button"
                  className={cn(
                    styles.dayCell,
                    !currentMonth && styles.dayOtherMonth,
                    isToday && styles.dayToday,
                    isSelected && styles.daySelected,
                    isDayDisabled && styles.dayDisabled,
                  )}
                  onClick={() => handleSelectDay(date)}
                  disabled={isDayDisabled}
                >
                  {date.getDate()}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {error && <span className={styles.errorText}>{error}</span>}
      {!error && helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  )
}
